/**
 * useUserRolesForOrg — reads the caller's grants in the active org
 * from public.profile_roles (the source of truth, not the cached JWT
 * array). Used by the RoleSwitcher to render the dropdown.
 *
 * RLS already restricts the read to the caller's own rows (see migration
 * 20260518000003), so no explicit user_id filter is needed — auth.uid()
 * gates the SELECT. The active org_id comes from the JWT we already
 * sourced through useAuthStore.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { useSupabase }                    from './useSupabase'
import { useAuthStore }                   from '@/store/authStore'

export interface UserRoleRow {
  organizationId: string
  role:           string
}

export function useUserRolesForOrg(): UseQueryResult<UserRoleRow[]> {
  const supabase = useSupabase()
  const orgId = useAuthStore((s) => {
    const meta = s.user?.app_metadata as { org_id?: string } | undefined
    return meta?.org_id ?? null
  })

  return useQuery({
    queryKey: ['profile-roles-active-org', orgId],
    enabled:  !!orgId,
    staleTime: 30_000,
    queryFn: async (): Promise<UserRoleRow[]> => {
      if (!orgId) return []
      const { data, error } = await supabase
        .from('profile_roles')
        .select('organization_id, role')
        .eq('organization_id', orgId)
      if (error) throw error
      const rows = (data ?? []) as Array<{ organization_id: string; role: string }>
      return rows.map((r) => ({ organizationId: r.organization_id, role: r.role }))
    },
  })
}
